import type { LocalStore } from "./LocalStore";

/**
 * Counts the entries of the given owners that the server has not received yet: clock events and
 * project attributions. Signing out deletes both from the device, so the warning before the
 * sign-out names them together (spec 11.4, F-57). The store counts through its index and reads no
 * entry (data minimization, F-84).
 *
 * @param store - Local store of the device.
 * @param owners - Owner keys of the person (one per tenant).
 * @returns The number of unsent entries.
 */
export async function countUnsentEntries(
  store: Pick<LocalStore, "countPending">,
  owners: readonly string[],
): Promise<number> {
  const distinct = [...new Set(owners)];
  const counts = await Promise.all(distinct.map((owner) => store.countPending(owner)));
  const total = counts.reduce((sum, count) => sum + count, 0);

  return total;
}

/**
 * Counts every entry on this device that the server has not received yet, of every person and
 * tenant. Signing out deletes all local data, so the warning before it names them all (F-84); it
 * also covers entries recorded in the application of the tenants before the account became
 * operator staff, for whom nothing is synchronized (F-69).
 *
 * @param store - Local store of the device.
 * @returns The number of unsent entries.
 */
export async function countUnsentEntriesOnDevice(
  store: Pick<LocalStore, "countPending" | "listPendingOwners">,
): Promise<number> {
  const owners = await store.listPendingOwners();
  const count = await countUnsentEntries(store, owners);

  return count;
}

/**
 * Returns whether this device holds settled data of a person from the application of the tenants
 * (F-84): roles from an answer of `GET /me`, or entries not yet sent. An account of operator staff
 * only never has either (`GET /me` answers TT-3001 and it records nothing), so the gate may open the
 * application of the tenants offline for such a person although the platform roles were never read.
 *
 * @param store - Local store of the device.
 * @param subject - OIDC subject of the person.
 * @returns `true` if such data exists.
 */
export async function hasTenantDataOnDevice(
  store: Pick<LocalStore, "hasRoleSnapshot" | "listPendingOwners">,
  subject: string,
): Promise<boolean> {
  const [snapshot, owners] = await Promise.all([
    store.hasRoleSnapshot(subject),
    store.listPendingOwners(),
  ]);
  const ownsEntries = owners.some((owner) => owner === subject || owner.startsWith(`${subject}@`));
  const found = snapshot || ownsEntries;

  return found;
}
